import { ADDRESS_ZERO } from '@uniswap/router-sdk';
import { Decimal } from 'decimal.js';
import { ethers } from 'ethers';
import { ElevateSdk } from '@/providers/elevateSdk/elevateSdk';
import type { Token } from '@/providers/elevateSdk/types';
import { isAllowedAsElevateTargetToken } from '@/providers/elevateSdk/utils';
import { CryptoAssetType, type CryptoAsset } from '@/types/WalletTypes';
import type {
  ExecuteSwapParams,
  GetMaxSwapAmountParams,
  GetSwapQuoteParams,
  ISwapProvider,
  MaxSwapAmountResult,
  SwapExecutionResult,
  SwapQuoteResult,
} from './ISwapProvider';

const ELEVATESWAP_CHAIN_ID = 1;

type ElevateSwapProviderOptions = {
  swapApiUrl?: string;
};

export class ElevateSwapProvider implements ISwapProvider {
  private elevateSdk: ElevateSdk;

  constructor({ swapApiUrl }: ElevateSwapProviderOptions) {
    this.elevateSdk = new ElevateSdk({ swapApiUrl });
  }

  async getSwapQuote({
    fromCryptoAsset,
    toCryptoAsset,
    amount,
    recipient,
    signer,
  }: GetSwapQuoteParams): Promise<SwapQuoteResult | null> {
    if (!this.isAllowedSellToken(fromCryptoAsset)) {
      throw new Error(
        `Token ${fromCryptoAsset.asset.canonicalName} is not enabled for swaps`
      );
    }

    if (!this.isAllowedBuyToken(toCryptoAsset)) {
      throw new Error(
        `Token ${toCryptoAsset.asset.canonicalName} is not allowed as a swap target`
      );
    }

    const quote = await this.elevateSdk.getQuote({
      fromToken: this.toElevateToken(fromCryptoAsset),
      toToken: this.toElevateToken(toCryptoAsset),
      amountInDecimal: amount.toString(),
      recipient,
      signer,
    });

    if (!quote) {
      return null;
    }

    return {
      estimatedAmountOutDecimal: quote.amountOutDecimal,
      estimatedGasFeeWei: quote.estimatedGasFeeWei,
    };
  }

  async getMaxSwapAmount({
    balance,
    precision,
    priceUsd,
    ...quoteParams
  }: GetMaxSwapAmountParams): Promise<MaxSwapAmountResult> {
    const balanceAmount = new Decimal(balance || 0);
    let maxCoin = balanceAmount;

    if (this.getSwapAssetType(quoteParams.fromCryptoAsset) === CryptoAssetType.Native) {
      const quote = await this.getSwapQuote({ ...quoteParams, amount: balance });
      const gasFee = quote?.estimatedGasFeeWei
        ? new Decimal(
            ethers.utils.formatUnits(
              quote.estimatedGasFeeWei,
              quoteParams.fromCryptoAsset.fee.decimalPrecision
            )
          )
        : new Decimal(0);
      maxCoin = Decimal.max(0, balanceAmount.minus(gasFee));
    }

    const formattedMaxCoin = maxCoin.toFixed(precision);
    const maxFiat =
      Number(priceUsd) > 0 ? maxCoin.mul(new Decimal(priceUsd)).toFixed(2) : null;

    return {
      maxCoin: formattedMaxCoin,
      maxFiat,
    };
  }

  async executeSwap({
    fromCryptoAsset,
    toCryptoAsset,
    amount,
    slippage = 0.3,
    wallet,
  }: ExecuteSwapParams): Promise<SwapExecutionResult> {
    if (!wallet?.mnemonic?.phrase) {
      throw new Error('Mnemonic-backed wallet is required for Elevate swaps');
    }

    if (!this.isAllowedSellToken(fromCryptoAsset)) {
      throw new Error(
        `Token ${fromCryptoAsset.asset.canonicalName} is not enabled for swaps`
      );
    }

    if (!this.isAllowedBuyToken(toCryptoAsset)) {
      throw new Error(
        `Token ${toCryptoAsset.asset.canonicalName} is not allowed as a swap target`
      );
    }

    const swapResult = await this.elevateSdk.executeSwap({
      mnemonic: wallet.mnemonic.phrase,
      fromToken: this.toElevateToken(fromCryptoAsset),
      toToken: this.toElevateToken(toCryptoAsset),
      amountInDecimal: amount.toString(),
      slippagePercentage: slippage,
    });

    return {
      transactionHash: swapResult.transactionHash,
    };
  }

  isAllowedBuyToken(cryptoAsset: CryptoAsset): boolean {
    if (!this.isConfiguredForSwapChain(cryptoAsset)) {
      return false;
    }

    return isAllowedAsElevateTargetToken({
      coinSymbol: cryptoAsset.asset.canonicalName,
      contractAddress: cryptoAsset.asset.definitionAddress || '',
      cryptoAsset,
    });
  }

  isAllowedSellToken(cryptoAsset: CryptoAsset): boolean {
    if (!this.isConfiguredForSwapChain(cryptoAsset)) {
      return false;
    }

    if (this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Native) {
      return true;
    }

    return cryptoAsset.asset.settings?.allowSell === true;
  }

  private toElevateToken(cryptoAsset: CryptoAsset): Token {
    const address =
      this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Native
        ? ADDRESS_ZERO
        : cryptoAsset.asset.definitionAddress;

    if (!address) {
      throw new Error(
        `Token ${cryptoAsset.asset.canonicalName} does not have a contract address for chain ${ELEVATESWAP_CHAIN_ID}`
      );
    }

    return {
      decimals: cryptoAsset.display.decimalPrecision,
      address,
      symbol: cryptoAsset.asset.canonicalName,
      chainId: ELEVATESWAP_CHAIN_ID,
    };
  }

  private getSwapAssetType(cryptoAsset: CryptoAsset): CryptoAssetType {
    return cryptoAsset.asset.assetType.id;
  }

  private isConfiguredForSwapChain(
    cryptoAsset?: CryptoAsset | null
  ): cryptoAsset is CryptoAsset {
    if (!cryptoAsset) return false;

    return (
      cryptoAsset.asset.blockchainConfiguration.chainId === ELEVATESWAP_CHAIN_ID &&
      (this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Native ||
        this.getSwapAssetType(cryptoAsset) === CryptoAssetType.Erc20)
    );
  }
}
